import React, { useState } from "react";
import { motion } from "framer-motion";
import { useSelector } from "react-redux";
import { RootState } from "../../redux/store";
import { CreditCard, Wallet, Gift, Users, Trophy } from "lucide-react";

// ─── Imported Tabs ───────────────────────────────────────────────
import Payouts from "./Payouts";
import PaymentMethods from "./PaymentMethods";
import Credits from "./Credits";
import Referrals from "./Referrals";
import Awards from "./Awards";

const PaymentMethodDashboard: React.FC = () => {
  const [activeTab, setActiveTab] = useState("payouts");
  const user = useSelector((state: RootState) => state.auth.user);

  const renderContent = () => {
    switch (activeTab) {
      case "payouts": return <Payouts />;
      case "methods": return <PaymentMethods />;

      case "credits": return <Credits />;
      case "referrals": return <Referrals />;
      case "awards": return <Awards />;
      default: return <PaymentMethods />;
    }
  };

  const tabs = [
    { id: "payouts", label: "Payouts", icon: <Wallet size={20} /> },
        { id: "methods", label: "Methods", icon: <CreditCard size={20} /> },

    { id: "credits", label: "Credits", icon: <Gift size={20} /> },
    { id: "referrals", label: "Referrals", icon: <Users size={20} /> },
   // { id: "awards", label: "Awards", icon: <Trophy size={20} /> },
  ];

  return (
    <div style={styles.wrapper}>
      {/* Header */}
      <header style={styles.header}>
        <h1 style={styles.title}>Payments Center</h1>
        {user && (
          <p style={styles.subTitle}>
            {user.firstName} {user.lastName} ·{" "}
            <span style={{ color: "#bf9b30" }}>
              {user.emailAddress?.replace(/(.{3}).+(@.+)/, "$1***$2")}
            </span>
          </p>
        )}
      </header>

      {/* Scrollable Icon Tabs */}
      <div style={styles.tabScroll}>
        <div style={styles.tabRow}>
          {tabs.map((tab) => (
            <motion.div
              key={tab.id}
              whileHover={{ scale: 1.15 }}
              whileTap={{ scale: 0.9 }}
              onClick={() => setActiveTab(tab.id)}
              style={
                activeTab === tab.id
                  ? { ...styles.tab, ...styles.activeTab }
                  : styles.tab
              }
            >
              <div style={styles.iconWrapper}>{tab.icon}</div>
              <span style={styles.label}>
                {tab.label}
              </span>
            </motion.div>
          ))}
        </div>
      </div>

      {/* Main Content */}
      <motion.div
        key={activeTab}
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
        style={styles.content}
      >
        {renderContent()}
      </motion.div>
    </div>
  );
};

export default PaymentMethodDashboard;

// ─── Styles ───────────────────────────────────────────────
const styles: { [key: string]: React.CSSProperties } = {
  wrapper: {
    minHeight: "100vh",
    backgroundColor: "#000",
    color: "#fff",
    fontFamily: "'Poppins', sans-serif",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
  },

  header: {
    textAlign: "center",
    padding: "40px 20px 20px",
    borderBottom: "1px solid rgba(191,155,48,0.3)",
    width: "100%",
  },

  title: {
    fontSize: 22,
    color: "#bf9b30",
    textTransform: "uppercase",
    fontWeight: 600,
    marginBottom: 6,
  },

  subTitle: {
    fontSize: 14,
    color: "#aaa",
    letterSpacing: 0.3,
  },

  tabScroll: {
    width: "100%",
    overflowX: "auto",
    whiteSpace: "nowrap",
    background: "linear-gradient(145deg, #0a0a0a, #111)",
    borderBottom: "1px solid rgba(191,155,48,0.25)",
    padding: "12px 0",
  },

  tabRow: {
    display: "flex",
    justifyContent: "center",
    gap: 28,
    padding: "0 20px",
  },

  tab: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: 6,
    flexShrink: 0,
    cursor: "pointer",
    color: "#aaa",
    transition: "all 0.3s ease",
  },

  activeTab: {
    color: "#bf9b30",
    textShadow: "0 0 12px rgba(191,155,48,0.8)",
  },

  iconWrapper: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: "linear-gradient(145deg, #0a0a0a, #111)",
    border: "1px solid rgba(191,155,48,0.25)",
    borderRadius: "50%",
    width: 48,
    height: 48,
    boxShadow: "inset 0 2px 6px rgba(0,0,0,0.6)",
  },

  label: {
    fontSize: 12,
    fontWeight: 500,
    letterSpacing: 0.5,
  },

  content: {
    width: "100%",
    maxWidth: 900,
    padding: "30px 20px",
    minHeight: "calc(100vh - 200px)",
  },
};
